import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/lib/firebase/firestore'
import { useAuth } from '@/hooks/useAuth'
import { formatUserFacingError } from '@/lib/errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { MediaPickerSheet } from '@/features/admin/components/MediaPickerSheet'
import { ProgramView } from './ProgramView'
import {
  downloadAsset,
  effectiveValues,
  PIECE_TYPES,
  programApi,
  publicSnapshot,
  validateDraft,
} from './api'
import type {
  ProgramDraft,
  ProgramSettings,
  ProgramItem,
  PieceValues,
  ItemField,
  FieldMapping,
  PublicProgram,
} from './api'
import type { VoteResults } from '../audienceAward/programVoting'
import type { SourceSubmission } from '../../../../functions/src/features/digitalPrograms/program.domain'
interface FieldOption {
  id: string
  label: string
  libraryId?: string
  source: 'intake' | 'technical'
}
export function ProgramAdminPage() {
  const { id: productionId = '', programId = '' } = useParams<{ id: string; programId: string }>()
  const { user } = useAuth()
  const [draft, setDraft] = useState<ProgramDraft | null>(null)
  const [published, setPublished] = useState<PublicProgram | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [preview, setPreview] = useState(false)
  const [pendingRevision, setPendingRevision] = useState(0)
  const [results, setResults] = useState<VoteResults | null>(null)
  const [fields, setFields] = useState<FieldOption[]>([])
  const [participants, setParticipants] = useState<SourceSubmission[]>([])
  const [picker, setPicker] = useState<{ kind: 'hero' | 'logo' | 'item'; itemId?: string } | null>(
    null
  )
  const load = useCallback(async () => {
    const response = await programApi.getAdmin(programId)
    if (response.draft.productionId !== productionId)
      throw new Error('Program belongs to another production.')
    setDraft(response.draft)
    setPublished(response.published)
    setPendingRevision(response.draft.revision)
  }, [programId, productionId])
  useEffect(() => {
    let active = true
    setBusy(true)
    void load()
      .catch((e) => {
        if (active) setError(formatUserFacingError(e, 'Program could not be loaded.'))
      })
      .finally(() => {
        if (active) setBusy(false)
      })
    return () => {
      active = false
    }
  }, [load])
  useEffect(
    () =>
      onSnapshot(
        doc(db, 'digitalPrograms', programId),
        (snapshot) => {
          if (snapshot.exists()) setPendingRevision(Number(snapshot.data().revision))
        },
        (e) => setError(formatUserFacingError(e, 'Draft updates unavailable.'))
      ),
    [programId]
  )
  useEffect(() => {
    let active = true
    void Promise.all([programApi.fields(productionId), programApi.submissions(productionId)])
      .then(([fields, participants]) => {
        if (active) {
          setFields(fields)
          setParticipants(participants.filter((s) => s.applicationType !== 'tech_questionnaire'))
        }
      })
      .catch((e) => {
        if (active) setError(formatUserFacingError(e, 'Source fields unavailable.'))
      })
    return () => {
      active = false
    }
  }, [productionId])
  useEffect(() => {
    setResults(null)
  }, [user?.uid])
  async function run(work: () => Promise<unknown>, success = 'Saved.') {
    setBusy(true)
    setError('')
    setMessage('')
    setResults(null)
    try {
      await work()
      await load()
      setMessage(success)
    } catch (e) {
      setError(formatUserFacingError(e, 'Action could not be completed. Reload and try again.'))
    } finally {
      setBusy(false)
    }
  }
  async function edit(data: Record<string, unknown>) {
    if (draft) await run(() => programApi.edit(draft.id, draft.revision, data))
  }
  async function move(index: number, delta: number) {
    if (!draft) return
    const ids = draft.items.map((i) => i.id)
    const other = index + delta
    if (other < 0 || other >= ids.length) return
    const current = ids[index]!
    ids[index] = ids[other]!
    ids[other] = current
    await edit({ action: 'reorder', ids })
  }
  async function assignMedia(mediaId: string, _url: string, focusX: number, focusY: number) {
    if (!draft || !picker) return
    const settings = structuredClone(draft.settings)
    if (picker.kind === 'item' && picker.itemId) {
      const item = draft.items.find((i) => i.id === picker.itemId)!
      settings.images[item.submissionId ?? item.id] = {
        mediaId,
        alt: `Image for ${effectiveValues(item).title ?? 'this piece'}`,
      }
    } else {
      settings.branding[picker.kind === 'hero' ? 'heroMediaId' : 'logoMediaId'] = mediaId
      if (picker.kind === 'hero') {
        settings.branding.heroFocusX = focusX
        settings.branding.heroFocusY = focusY
      }
    }
    setPicker(null)
    await edit({ action: 'settings', settings })
  }
  async function asset(kind: string) {
    if (!draft) return
    setBusy(true)
    setError('')
    try {
      downloadAsset(await programApi.asset(draft.id, kind, !published))
      setMessage(`${published ? 'Published' : 'Draft preview'} asset downloaded.`)
    } catch (e) {
      setError(formatUserFacingError(e, 'Asset could not be generated.'))
    } finally {
      setBusy(false)
    }
  }
  async function readResults() {
    setResults(null)
    setBusy(true)
    setError('')
    try {
      setResults(await programApi.results(programId))
    } catch (e) {
      setError(formatUserFacingError(e, 'Administrator authorization is required for results.'))
    } finally {
      setBusy(false)
    }
  }
  if (!draft)
    return (
      <div className="p-6">
        <p role="status">{busy ? 'Loading program…' : error || 'Program unavailable.'}</p>
        <Button onClick={() => void run(load)}>Retry</Button>
      </div>
    )
  const validation = validateDraft(draft)
  const blocked = validation.some((v) => v.blocking)
  const changed =
    !published ||
    JSON.stringify({ ...publicSnapshot(draft, 0), version: 0 }) !==
      JSON.stringify({ ...published, version: 0 })
  const stale = pendingRevision !== draft.revision
  return (
    <div className="space-y-6 py-4">
      <Link
        className="inline-block min-h-11 py-3 text-sm underline"
        to={`/admin/productions/${productionId}`}
      >
        ← Production command center
      </Link>
      <div>
        <h1 className="text-3xl font-bold">Digital Program</h1>
        <p className="mt-2 break-all">
          /program/{draft.slug}/ ·{' '}
          {published ? `Published version ${published.version}` : 'Unpublished'} ·{' '}
          {changed ? 'Draft differs from published program' : 'Draft matches published program'}
        </p>
      </div>
      {error && (
        <p className="rounded border border-destructive p-3 text-destructive" role="alert">
          {error}
        </p>
      )}
      {message && <p role="status">{message}</p>}
      {stale && (
        <div className="rounded border border-amber-500 p-4" role="status">
          Source data or another editor changed this draft. Reload before saving; current form input
          remains on screen.
          <Button
            className="ml-3"
            disabled={busy}
            onClick={() => void run(load, 'Draft reloaded.')}
          >
            Reload draft
          </Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => void run(load, 'Sources refreshed.')}
        >
          Refresh source data
        </Button>
        <Button variant="outline" onClick={() => setPreview((p) => !p)}>
          {preview ? 'Hide preview' : 'Preview approved fields'}
        </Button>
        {published && (
          <Button asChild variant="outline">
            <a href={`/program/${draft.slug}/`} target="_blank" rel="noreferrer">
              View published program
            </a>
          </Button>
        )}
        <Button
          disabled={busy || blocked || stale}
          onClick={() => {
            if (window.confirm(`Publish the reviewed draft at /program/${draft.slug}/?`))
              void run(
                () => programApi.publish(draft.id, draft.revision),
                'Program published. Voting availability is unchanged.'
              )
          }}
        >
          Publish reviewed draft
        </Button>
        {published && (
          <Button
            variant="destructive"
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  'Unpublish the program and close voting? Existing versions and ballots are preserved.'
                )
              )
                void run(
                  () => programApi.unpublish(draft.id),
                  'Program unpublished; voting closed.'
                )
            }}
          >
            Unpublish
          </Button>
        )}
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Validation and source warnings</CardTitle>
        </CardHeader>
        <CardContent>
          {validation.length === 0 ? (
            <p>No unresolved validation warnings.</p>
          ) : (
            <ul className="space-y-2">
              {validation.map((v, i) => (
                <li key={i} className={v.blocking ? 'text-destructive' : 'text-muted-foreground'}>
                  {v.itemId && (
                    <a className="underline" href={`#edit-${v.itemId}`}>
                      {v.itemId}:{' '}
                    </a>
                  )}
                  {v.field && `${v.field}: `}
                  {v.message}
                  {v.blocking ? ' (blocks publishing)' : ''}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <SettingsEditor
        key={`settings-${draft.revision}`}
        draft={draft}
        fields={fields}
        participants={participants}
        disabled={busy || stale}
        save={(settings) => edit({ action: 'settings', settings })}
      />
      <Card>
        <CardHeader>
          <CardTitle>Approved branding</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            disabled={busy || stale}
            onClick={() => setPicker({ kind: 'hero' })}
          >
            Choose hero image from media library
          </Button>
          <Button
            variant="outline"
            disabled={busy || stale}
            onClick={() => setPicker({ kind: 'logo' })}
          >
            Choose show logo from media library
          </Button>
          {(['hero', 'logo'] as const).map(
            (kind) =>
              draft.branding[kind] && (
                <div key={kind}>
                  <img
                    className="h-28 max-w-48 object-contain"
                    src={draft.branding[kind]!.url}
                    alt={draft.branding[kind]!.alt}
                  />
                  <Button
                    variant="ghost"
                    disabled={busy || stale}
                    onClick={() => {
                      const settings = structuredClone(draft.settings)
                      delete settings.branding[kind === 'hero' ? 'heroMediaId' : 'logoMediaId']
                      void edit({ action: 'settings', settings })
                    }}
                  >
                    Reset {kind}
                  </Button>
                </div>
              )
          )}
        </CardContent>
      </Card>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-2xl font-bold">Program order</h2>
        <Button
          disabled={busy || stale || draft.items.length >= 80}
          onClick={() => void edit({ action: 'add', itemId: crypto.randomUUID() })}
        >
          Add organizer piece
        </Button>
      </div>
      {draft.items.map((item, index) => (
        <ItemEditor
          key={`${item.id}-${draft.revision}`}
          item={item}
          index={index}
          last={index === draft.items.length - 1}
          disabled={busy || stale}
          save={(data) => edit(data)}
          move={(delta) => move(index, delta)}
          image={() => setPicker({ kind: 'item', itemId: item.id })}
        />
      ))}
      <Card>
        <CardHeader>
          <CardTitle>QR and screen assets</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">
            Downloads use {published ? 'the published snapshot' : 'approved draft preview branding'}
            . QR codes always point to the stable public URL. Preview downloads do not publish the
            program.
          </p>
          <div className="flex flex-wrap gap-2">
            {['qr', 'welcome', ...(draft.settings.votingEnabled ? ['vote'] : [])].map((kind) => (
              <Button
                key={kind}
                variant="outline"
                disabled={busy || (!published && blocked)}
                onClick={() => void asset(kind)}
              >
                Download {kind === 'qr' ? 'QR code' : `${kind} screen`}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
      {draft.settings.votingEnabled && (
        <Card>
          <CardHeader>
            <CardTitle>Audience award, private organizer controls</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              Voting starts closed. First opening freezes eligible item IDs, titles, and order for
              this award. Closing and reopening preserve ballots. Browser tokens limit ballots per
              browser, and do not guarantee one vote per person.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={busy || !published}
                onClick={() => {
                  if (window.confirm('Open audience voting and freeze this award roster?'))
                    void run(
                      () => programApi.setOpen(draft.id, true),
                      'Voting opened; roster frozen.'
                    )
                }}
              >
                Open voting
              </Button>
              <Button
                variant="outline"
                disabled={busy}
                onClick={() =>
                  void run(
                    () => programApi.setOpen(draft.id, false),
                    'Voting closed; ballots preserved.'
                  )
                }
              >
                Close voting
              </Button>
              <Button variant="outline" disabled={busy} onClick={() => void readResults()}>
                Load private results
              </Button>
            </div>
            {results && (
              <div>
                <p>
                  {results.isOpen ? 'Voting open' : 'Voting closed'} · {results.total} ballots
                </p>
                <table className="w-full text-left">
                  <caption className="sr-only">Private award counts</caption>
                  <thead>
                    <tr>
                      <th>Piece</th>
                      <th>Votes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.films.map((f) => (
                      <tr key={f.id}>
                        <td className="py-2">
                          {f.title}
                          {results.leaders.includes(f.id) ? ' (leader)' : ''}
                        </td>
                        <td>{f.votes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {preview && (
        <section aria-label="Approved draft preview">
          <ProgramView program={publicSnapshot(draft, 0)} preview />
        </section>
      )}
      <MediaPickerSheet
        open={!!picker}
        onClose={() => setPicker(null)}
        title="Approve program imagery"
        description="Select a processed image for public use. Applicant file links are never used."
        mimeTypeFilter="image/"
        withFocusPoint={picker?.kind === 'hero'}
        onAssign={(id, url, x, y) => void assignMedia(id, url, x, y)}
      />
    </div>
  )
}
function SettingsEditor({
  draft,
  fields,
  participants,
  disabled,
  save,
}: {
  draft: ProgramDraft
  fields: FieldOption[]
  participants: SourceSubmission[]
  disabled: boolean
  save: (settings: ProgramSettings) => Promise<void>
}) {
  const [settings, setSettings] = useState(() => structuredClone(draft.settings))
  function changeMapping(field: keyof ProgramSettings['mappings'], patch: Partial<FieldMapping>) {
    setSettings((s) => ({
      ...s,
      mappings: {
        ...s.mappings,
        [field]: { source: s.mappings[field]?.source ?? 'intake', ...s.mappings[field], ...patch },
      },
    }))
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Show details, mappings, and performance assignment</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault()
            void save(settings)
          }}
        >
          <fieldset disabled={disabled} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              {(['title', 'startsAt', 'venue', 'description', 'timeZone'] as const).map((field) => (
                <div key={field}>
                  <Label htmlFor={`show-${field}`}>
                    {field === 'startsAt'
                      ? 'Date and time (ISO 8601 with timezone)'
                      : field === 'timeZone'
                        ? 'Display timezone (IANA)'
                        : `Show ${field}`}
                  </Label>
                  <Input
                    id={`show-${field}`}
                    value={settings.showOverrides[field] ?? draft.showSource[field]}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        showApproved: false,
                        showOverrides: { ...s.showOverrides, [field]: e.target.value },
                      }))
                    }
                  />
                  <p className="break-all text-sm text-muted-foreground">
                    Source: {draft.showSource[field] || 'Missing'}
                  </p>
                  {settings.showOverrides[field] !== undefined && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const overrides = { ...settings.showOverrides }
                        delete overrides[field]
                        setSettings((s) => ({
                          ...s,
                          showOverrides: overrides,
                          showApproved: false,
                        }))
                      }}
                    >
                      Reset to source
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <label className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={settings.showApproved}
                onChange={(e) => setSettings((s) => ({ ...s, showApproved: e.target.checked }))}
              />
              I approve these show details for public display
            </label>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="default-piece-type">Imported piece type</Label>
                <select
                  id="default-piece-type"
                  className="min-h-11 w-full rounded border bg-background p-2"
                  value={settings.defaultPieceType}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      defaultPieceType: e.target.value as ProgramSettings['defaultPieceType'],
                    }))
                  }
                >
                  {PIECE_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="accent">Accent color</Label>
                <Input
                  id="accent"
                  value={settings.branding.accent}
                  pattern="#[a-fA-F0-9]{6}"
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      branding: { ...s.branding, accent: e.target.value },
                    }))
                  }
                />
              </div>
              <label className="flex min-h-11 items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.votingEnabled}
                  onChange={(e) => setSettings((s) => ({ ...s, votingEnabled: e.target.checked }))}
                />
                Enable audience award
              </label>
            </div>
            <h3 className="font-semibold">Explicit source field mappings</h3>
            <p className="text-sm">
              Choose stable field IDs or library IDs. Labels help you find fields and are never used
              to infer answers. Durations need an explicit unit.
            </p>
            {(
              [
                'title',
                'creator',
                'description',
                'credits',
                'biography',
                'durationSeconds',
              ] as const
            ).map((field) => {
              const mapping = settings.mappings[field]
              const source = mapping?.source ?? 'intake'
              return (
                <div className="grid gap-2 rounded border p-3 sm:grid-cols-3" key={field}>
                  <Label className="self-center" htmlFor={`mapping-${field}`}>
                    {field}
                  </Label>
                  <select
                    className="min-h-11 rounded border bg-background p-2"
                    aria-label={`${field} source`}
                    value={source}
                    onChange={(e) => {
                      const mappings = { ...settings.mappings }
                      delete mappings[field]
                      setSettings((s) => ({
                        ...s,
                        mappings: {
                          ...mappings,
                          [field]: { source: e.target.value as FieldMapping['source'] },
                        },
                      }))
                    }}
                  >
                    <option value="intake">Intake application</option>
                    <option value="technical">Technical questionnaire</option>
                  </select>
                  <select
                    id={`mapping-${field}`}
                    className="min-h-11 min-w-0 rounded border bg-background p-2"
                    value={
                      mapping?.fieldId
                        ? `field:${mapping.fieldId}`
                        : mapping?.libraryId
                          ? `library:${mapping.libraryId}`
                          : ''
                    }
                    onChange={(e) => {
                      const [kind, id] = e.target.value.split(':')
                      if (!id) {
                        const mappings = { ...settings.mappings }
                        delete mappings[field]
                        setSettings((s) => ({ ...s, mappings }))
                        return
                      }
                      const next: FieldMapping = {
                        source,
                        ...(kind === 'field' ? { fieldId: id } : { libraryId: id }),
                        ...(mapping?.unit ? { unit: mapping.unit } : {}),
                      }
                      setSettings((s) => ({ ...s, mappings: { ...s.mappings, [field]: next } }))
                    }}
                  >
                    <option value="">No mapping (use an override)</option>
                    {fields
                      .filter((f) => f.source === source)
                      .map((f) => (
                        <option key={`field:${f.id}`} value={`field:${f.id}`}>
                          {f.label} [field: {f.id}]
                        </option>
                      ))}
                    {fields
                      .filter((f) => f.source === source && f.libraryId)
                      .filter(
                        (f, i, all) => all.findIndex((a) => a.libraryId === f.libraryId) === i
                      )
                      .map((f) => (
                        <option key={`library:${f.libraryId}`} value={`library:${f.libraryId}`}>
                          {f.label} [library: {f.libraryId}]
                        </option>
                      ))}
                    {mapping?.fieldId &&
                      !fields.some((f) => f.source === source && f.id === mapping.fieldId) && (
                        <option value={`field:${mapping.fieldId}`}>
                          Saved field {mapping.fieldId} (not in current form)
                        </option>
                      )}
                    {mapping?.libraryId &&
                      !fields.some(
                        (f) => f.source === source && f.libraryId === mapping.libraryId
                      ) && (
                        <option value={`library:${mapping.libraryId}`}>
                          Saved library {mapping.libraryId}
                        </option>
                      )}
                  </select>
                  {field === 'durationSeconds' && mapping && (
                    <select
                      aria-label="Duration source unit"
                      className="min-h-11 rounded border bg-background p-2"
                      value={mapping.unit ?? ''}
                      onChange={(e) =>
                        changeMapping(field, { unit: e.target.value as 'seconds' | 'minutes' })
                      }
                    >
                      <option value="">Choose unit</option>
                      <option value="seconds">Seconds</option>
                      <option value="minutes">Minutes</option>
                    </select>
                  )}
                </div>
              )
            })}
            <h3 className="font-semibold">Participants for this performance</h3>
            <label className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={settings.participantIds === null}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, participantIds: e.target.checked ? null : [] }))
                }
              />
              Automatically include confirmed participants (only valid with one performance)
            </label>
            {settings.participantIds !== null && (
              <div className="space-y-2">
                {participants.map((s) => (
                  <label key={s.id} className="flex min-h-11 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.participantIds!.includes(s.id)}
                      onChange={(e) =>
                        setSettings((v) => ({
                          ...v,
                          participantIds: e.target.checked
                            ? [...(v.participantIds ?? []), s.id]
                            : (v.participantIds ?? []).filter((id) => id !== s.id),
                        }))
                      }
                    />
                    <span>
                      {String(s.answers?.[settings.mappings.title?.fieldId ?? '']?.value ?? s.id)} ·{' '}
                      {s.participantOfferStatus ??
                        (s.participantAcceptedAt ? 'Accepted participation' : 'Awaiting response')}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <Button type="submit">Save show settings and regenerate</Button>
          </fieldset>
        </form>
      </CardContent>
    </Card>
  )
}
function ItemEditor({
  item,
  index,
  last,
  disabled,
  save,
  move,
  image,
}: {
  item: ProgramItem
  index: number
  last: boolean
  disabled: boolean
  save: (data: Record<string, unknown>) => Promise<void>
  move: (delta: number) => Promise<void>
  image: () => void
}) {
  const [overrides, setOverrides] = useState<PieceValues>(() => structuredClone(item.overrides))
  const [approved, setApproved] = useState<ItemField[]>(item.approvedFields)
  const [excluded, setExcluded] = useState(item.excluded ?? false)
  const values = { ...item.source, ...overrides }
  function set(field: ItemField, value: unknown) {
    setOverrides((v) => ({ ...v, [field]: value }))
    setApproved((fields) => fields.filter((f) => f !== field))
  }
  return (
    <Card id={`edit-${item.id}`}>
      <CardHeader>
        <div className="flex flex-wrap justify-between gap-3">
          <CardTitle>
            {index + 1}. {values.title || 'Untitled piece'}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={disabled || index === 0}
              onClick={() => void move(-1)}
            >
              Move up
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={disabled || last}
              onClick={() => void move(1)}
            >
              Move down
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {item.origin === 'manual'
            ? 'Organizer-added piece'
            : `Imported submission ${item.submissionId}`}{' '}
          · Stable ID: {item.id}
          {item.inactiveSource ? ' · Source participant no longer confirmed' : ''}
        </p>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void save({
              action: 'item',
              itemId: item.id,
              overrides,
              approvedFields: approved,
              excluded,
            })
          }}
        >
          <fieldset disabled={disabled || item.inactiveSource} className="space-y-4">
            {(
              [
                'title',
                'pieceType',
                'creator',
                'description',
                'credits',
                'biography',
                'durationSeconds',
                'awardEligible',
              ] as const
            ).map((field) => (
              <div className="rounded border p-3" key={field}>
                <Label htmlFor={`${item.id}-${field}`}>
                  {field === 'biography'
                    ? 'Artist biography (optional)'
                    : field === 'durationSeconds'
                      ? 'Duration in seconds (optional)'
                      : field}
                </Label>
                {field === 'pieceType' ? (
                  <select
                    id={`${item.id}-${field}`}
                    className="min-h-11 w-full rounded border bg-background p-2"
                    value={values.pieceType ?? 'live'}
                    onChange={(e) => set(field, e.target.value)}
                  >
                    {PIECE_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                ) : field === 'awardEligible' ? (
                  <label className="flex min-h-11 items-center gap-2">
                    <input
                      id={`${item.id}-${field}`}
                      type="checkbox"
                      checked={values.awardEligible ?? false}
                      onChange={(e) => set(field, e.target.checked)}
                    />
                    Eligible for this performance’s audience award
                  </label>
                ) : field === 'durationSeconds' ? (
                  <Input
                    id={`${item.id}-${field}`}
                    type="number"
                    min="0"
                    step="any"
                    value={values.durationSeconds ?? ''}
                    onChange={(e) =>
                      set(field, e.target.value === '' ? null : Number(e.target.value))
                    }
                  />
                ) : ['description', 'credits', 'biography'].includes(field) ? (
                  <Textarea
                    id={`${item.id}-${field}`}
                    rows={field === 'credits' ? 6 : 3}
                    value={String(values[field] ?? '')}
                    onChange={(e) => set(field, e.target.value)}
                  />
                ) : (
                  <Input
                    id={`${item.id}-${field}`}
                    value={String(values[field] ?? '')}
                    onChange={(e) => set(field, e.target.value)}
                  />
                )}
                <p className="my-2 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                  {Object.hasOwn(overrides, field) ? 'Administrator override' : 'Imported source'} ·
                  Source: {String(item.source[field] ?? 'Missing')}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex min-h-11 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={approved.includes(field)}
                      onChange={(e) =>
                        setApproved((v) =>
                          e.target.checked ? [...v, field] : v.filter((f) => f !== field)
                        )
                      }
                    />
                    Approve this field for public display
                  </label>
                  {Object.hasOwn(overrides, field) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const next = { ...overrides }
                        delete next[field]
                        setOverrides(next)
                        setApproved((v) => v.filter((f) => f !== field))
                      }}
                    >
                      Reset to source
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <div className="rounded border p-3">
              <Label>Approved piece imagery</Label>
              {values.image && (
                <img
                  className="my-3 h-auto max-h-48 max-w-full object-contain"
                  src={values.image.url}
                  alt={values.image.alt}
                />
              )}
              <Button type="button" variant="outline" onClick={image}>
                Choose approved media
              </Button>
              <label className="ml-3 inline-flex min-h-11 items-center gap-2">
                <input
                  type="checkbox"
                  checked={approved.includes('image')}
                  onChange={(e) =>
                    setApproved((v) =>
                      e.target.checked ? [...v, 'image'] : v.filter((f) => f !== 'image')
                    )
                  }
                />
                Approve image for public display
              </label>
              {values.image && (
                <Button type="button" variant="ghost" onClick={() => set('image', null)}>
                  Hide image
                </Button>
              )}
              {Object.hasOwn(overrides, 'image') && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    const v = { ...overrides }
                    delete v.image
                    setOverrides(v)
                    setApproved((a) => a.filter((f) => f !== 'image'))
                  }}
                >
                  Reset image to source
                </Button>
              )}
            </div>
            <label className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={excluded}
                onChange={(e) => setExcluded(e.target.checked)}
              />
              Exclude from this program (keep edits privately)
            </label>
            <div className="flex flex-wrap gap-2">
              <Button type="submit">Save piece and public approvals</Button>
              {item.origin === 'manual' && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    if (
                      window.confirm(
                        'Remove this organizer-added piece from the draft? Published versions are preserved.'
                      )
                    )
                      void save({ action: 'delete', itemId: item.id })
                  }}
                >
                  Remove organizer piece
                </Button>
              )}
            </div>
          </fieldset>
        </form>
      </CardContent>
    </Card>
  )
}
